import type { LatLng } from '@/state/types';
import { haversineKm, lineStringLengthKm } from './geo';

/**
 * Phase 5 routing facade.
 *
 *   - `fetchRoute(from, to)` queries OSRM Public Demo. On any error / rate-limit
 *     the function silently falls back to a straight-line route (haversine).
 *   - In-memory LRU cache (key = "lat,lng_lat,lng" rounded to 4 decimals).
 *     Session-only by design — reload starts cold.
 *   - `estimateEtaSec` / `formatEtaSec` provide UI-side helpers.
 *
 * Speed model (per user spec):
 *   Status 3 / 7 (emergency) → 60 km/h × spec.speedMultiplier
 *   Status 1   (return)      → 40 km/h × spec.speedMultiplier
 *   Game-speed multiplier is already baked into gameTime ticks.
 */

export const EMERGENCY_KMH = 60;
export const RETURN_KMH = 40;
export const STRAIGHT_LINE_DETOUR_FACTOR = 1.3;
/**
 * When OSRM is unavailable, the fallback "route" is a straight line. Effective
 * speed gets multiplied by this factor to approximate the detour penalty
 * a real road network would have imposed.
 */
export const FALLBACK_SPEED_FACTOR = 0.7;

export const MAX_ROUTE_CACHE_SIZE = 500;
export const OSRM_BASE_URL = 'https://router.project-osrm.org';

export type LegType = 'emergency' | 'return';

export interface RouteResult {
  /** GeoJSON [lng, lat] pairs. */
  coords: [number, number][];
  /** Total length in km (from haversine over coord pairs). */
  distanceKm: number;
  /** True if the result came from OSRM, false if fallback was used. */
  fromOsrm: boolean;
}

/* ─────────────────────────── LRU cache ─────────────────────────── */

const cache = new Map<string, RouteResult>();

function cacheKey(from: LatLng, to: LatLng): string {
  return `${from.lat.toFixed(4)},${from.lng.toFixed(4)}_${to.lat.toFixed(4)},${to.lng.toFixed(4)}`;
}

function cacheGet(key: string): RouteResult | undefined {
  const v = cache.get(key);
  if (v === undefined) return undefined;
  // LRU: re-insert to mark as most recently used
  cache.delete(key);
  cache.set(key, v);
  return v;
}

function cacheSet(key: string, value: RouteResult): void {
  if (cache.has(key)) cache.delete(key);
  cache.set(key, value);
  if (cache.size > MAX_ROUTE_CACHE_SIZE) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
  }
}

/** Test-only: clear cache between tests. */
export function _clearRouteCacheForTests(): void {
  cache.clear();
}

/** Test-only: peek cache size. */
export function _routeCacheSize(): number {
  return cache.size;
}

/* ─────────────────────────── Fetch + fallback ─────────────────────────── */

interface OsrmResponse {
  code: string;
  routes?: { geometry: { coordinates: [number, number][] } }[];
}

async function fetchFromOsrm(from: LatLng, to: LatLng): Promise<RouteResult> {
  const url = `${OSRM_BASE_URL}/route/v1/driving/${from.lng},${from.lat};${to.lng},${to.lat}?overview=full&geometries=geojson`;
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`OSRM HTTP ${resp.status}`);
  const data = (await resp.json()) as OsrmResponse;
  if (data.code !== 'Ok' || !data.routes?.[0]) {
    throw new Error(`OSRM: no route (code=${data.code})`);
  }
  const coords = data.routes[0].geometry.coordinates;
  return {
    coords,
    distanceKm: lineStringLengthKm(coords),
    fromOsrm: true,
  };
}

function fallbackRoute(from: LatLng, to: LatLng): RouteResult {
  return {
    coords: [
      [from.lng, from.lat],
      [to.lng, to.lat],
    ],
    distanceKm: haversineKm(from, to),
    fromOsrm: false,
  };
}

/**
 * Resolve a route between two coordinates. Always succeeds — falls back
 * silently to a straight line on OSRM failure. Caches results.
 */
export async function fetchRoute(from: LatLng, to: LatLng): Promise<RouteResult> {
  const key = cacheKey(from, to);
  const cached = cacheGet(key);
  if (cached !== undefined) return cached;

  try {
    const result = await fetchFromOsrm(from, to);
    cacheSet(key, result);
    return result;
  } catch (err) {
    console.warn('OSRM unavailable, using straight-line fallback:', err);
    const result = fallbackRoute(from, to);
    cacheSet(key, result);
    return result;
  }
}

/* ─────────────────────────── Duration computations ─────────────────────────── */

/**
 * Effective travel time in milliseconds for a vehicle covering `distanceKm`.
 * Fallback routes (crow-flight) get scaled down by FALLBACK_SPEED_FACTOR
 * to approximate real road detour delay.
 */
export function computeLegDurationMs(
  distanceKm: number,
  speedMultiplier: number,
  legType: LegType,
  fromOsrm: boolean,
): number {
  const baseKmh = legType === 'emergency' ? EMERGENCY_KMH : RETURN_KMH;
  let effectiveKmh = baseKmh * speedMultiplier;
  if (!fromOsrm) effectiveKmh *= FALLBACK_SPEED_FACTOR;
  if (effectiveKmh <= 0) return Number.POSITIVE_INFINITY;
  return Math.round((distanceKm / effectiveKmh) * 3_600_000);
}

/**
 * UI-side ETA estimate (no network call). Used in the Disposition panel.
 * Uses haversine × detour factor as a stable approximation.
 */
export function estimateEtaSec(
  from: LatLng,
  to: LatLng,
  speedMultiplier = 1,
): number {
  const distanceKm = haversineKm(from, to) * STRAIGHT_LINE_DETOUR_FACTOR;
  const effectiveKmh = EMERGENCY_KMH * speedMultiplier;
  if (effectiveKmh <= 0) return Number.POSITIVE_INFINITY;
  return Math.round((distanceKm / effectiveKmh) * 3600);
}

/** Format an ETA for the dispatch panel: "12 s" / "1 min 12 s" / "10 min". */
export function formatEtaSec(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '—';
  if (sec < 60) return `${Math.round(sec)} s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  if (s === 0 || m >= 10) return `${m} min`;
  return `${m} min ${s} s`;
}
